"use client";

import * as React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Checkbox } from "./ui/checkbox";
import { Progress } from "./ui/progress";
import { Badge } from "./ui/badge";
import { AstroMascot } from "./AstroMascot";
import { ScrollArea } from "./ui/scroll-area";
import { 
  ArrowLeft, 
  // FileText, 
  Download,
  ExternalLink,
  Shield,
  CheckCircle2,
  Info,
  AlertCircle,
  Clock,
  Eye,
  Star,
  ChevronDown,
  ChevronUp,
  Sparkles,
  BookOpen,
  Target
} from "lucide-react";
const openTurfLogo = '/openturf-logo.svg';

interface TermsAndConditionsProps {
  onBack?: () => void;
  onAccept?: () => void;
  onShowPrivacyPolicy?: () => void;
}

interface KeyPoint {
  id: string;
  title: string;
  summary: string;
  details: string;
  importance: 'high' | 'medium' | 'low';
  section: number;
}

const keyPoints: KeyPoint[] = [
  {
    id: 'data-collection',
    title: 'Data Collection',
    summary: 'We collect personal and employment information for onboarding',
    details: 'This includes your ID documents, educational certificates, work experience, and contact information. All data is encrypted and used solely for employment verification.',
    importance: 'high',
    section: 3
  },
  {
    id: 'ai-processing',
    title: 'AI Document Processing',
    summary: 'AI extracts information from your documents',
    details: 'Our TurfAI technology automatically reads your documents to speed up onboarding. You can always review and correct any extracted information.',
    importance: 'high',
    section: 4
  },
  {
    id: 'data-security',
    title: 'Data Security',
    summary: 'Enterprise-grade security protects your information',
    details: 'We use encryption, secure cloud infrastructure, and restrict access to authorized personnel only. Your data is safe with us.',
    importance: 'medium',
    section: 6
  },
  {
    id: 'user-responsibilities',
    title: 'Your Responsibilities',
    summary: 'Provide accurate information and genuine documents',
    details: 'Please ensure all uploaded documents are authentic and information is accurate. This helps us process your onboarding quickly and securely.',
    importance: 'medium',
    section: 5
  }
];

export function TermsAndConditions({ 
  onBack, 
  onAccept,
  onShowPrivacyPolicy 
}: TermsAndConditionsProps) {
  const [hasAccepted, setHasAccepted] = React.useState(false);
  const [readingProgress, setReadingProgress] = React.useState(0);
  const [readingSections, setReadingSections] = React.useState<Set<number>>(new Set());
  const [expandedKeyPoint, setExpandedKeyPoint] = React.useState<string | null>(null);
  const [startTime] = React.useState(Date.now());
  const [readingTime, setReadingTime] = React.useState(0);
  const [hasStartedReading, setHasStartedReading] = React.useState(false);
  const [astroMessage, setAstroMessage] = React.useState("professional");
  const [showCelebration, setShowCelebration] = React.useState(false);

  // Reading time tracker
  React.useEffect(() => {
    if (hasStartedReading) {
      const interval = setInterval(() => {
        setReadingTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [hasStartedReading, startTime]);

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    const progress = (scrollTop / (scrollHeight - clientHeight)) * 100;
    setReadingProgress(Math.min(progress, 100));

    if (!hasStartedReading && scrollTop > 50) {
      setHasStartedReading(true);
    }

    // Track which sections are being read
    const currentSection = Math.floor((scrollTop / (scrollHeight - clientHeight)) * 10) + 1;
    if (currentSection <= 10) {
      setReadingSections(prev => new Set([...prev, currentSection]));
    }

    // Update Astro message based on progress
    if (progress > 75 && astroMessage === "professional") {
      setAstroMessage("Almost there! You're doing great.");
    } else if (progress > 50 && astroMessage === "professional") {
      setAstroMessage("Halfway through! The important parts are covered.");
    } else if (progress > 25 && astroMessage === "professional") {
      setAstroMessage("Great start! I see you're reading carefully.");
    }
  };

  const handleDownloadPDF = () => {
    // Simulate PDF download
    const link = document.createElement('a');
    link.href = 'data:application/pdf;base64,'; // In real app, this would be actual PDF
    link.download = 'OpenTurf_Terms_and_Conditions.pdf';
    link.click();
  };

  const handleAcceptTerms = () => {
    if (hasAccepted && onAccept) {
      setShowCelebration(true);
      setTimeout(() => {
        onAccept();
      }, 1000);
    }
  };

  const toggleKeyPoint = (id: string) => {
    setExpandedKeyPoint(expandedKeyPoint === id ? null : id);
  };

  const formatReadingTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const completionPercentage = (readingSections.size / 10) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex flex-col relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-32 h-32 bg-blue-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-slate-200/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/4 w-24 h-24 bg-indigo-200/15 rounded-full blur-2xl" />
      </div>

      {/* Celebration overlay */}
      <AnimatePresence>
        {showCelebration && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-blue-600/20 backdrop-blur-sm flex items-center justify-center"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              className="bg-white rounded-3xl p-8 shadow-2xl text-center"
            >
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 0.5, repeat: 2 }}
              >
                <Sparkles className="w-16 h-16 text-blue-600 mx-auto mb-4" />
              </motion.div>
              <h3 className="text-slate-900 mb-2">Terms Accepted! 🎉</h3>
              <p className="text-slate-600">Ready for your onboarding journey!</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header with Reading Progress */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 p-6 border-b border-gray-200/50 bg-white/70 backdrop-blur-sm"
      >
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={onBack}
                className="text-slate-700 hover:text-slate-900 hover:bg-slate-100"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5 text-blue-600" />
                <span className="text-slate-900">Legal Agreement</span>
              </div>
            </div>
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-8 w-auto"
            />
          </div>

          {/* Reading Progress Bar */}
          {hasStartedReading && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="space-y-2"
            >
              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center space-x-2">
                  <BookOpen className="w-4 h-4 text-blue-600" />
                  <span className="text-slate-600">Reading Progress</span>
                </div>
                <div className="flex items-center space-x-4 text-xs text-slate-500">
                  <div className="flex items-center space-x-1">
                    <Clock className="w-3 h-3" />
                    <span>{formatReadingTime(readingTime)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Target className="w-3 h-3" />
                    <span>{Math.round(completionPercentage)}% complete</span>
                  </div>
                </div>
              </div>
              <Progress value={readingProgress} className="h-2" />
            </motion.div>
          )}
        </div>
      </motion.header>

      {/* Main content */}
      <div className="flex-1 p-6 min-h-0">
        <div className="max-w-5xl mx-auto h-full">
          {/* Enhanced Astro Introduction */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="mb-8"
          >
            <Card className="bg-gradient-to-r from-blue-50 to-slate-50 border-blue-200 p-6 relative overflow-hidden">
              <div className="flex items-start space-x-6">
                <motion.div
                  animate={{ 
                    y: [0, -8, 0],
                    rotate: [0, 2, -2, 0]
                  }}
                  transition={{ 
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <AstroMascot expression="professional" size="lg" />
                </motion.div>
                
                <div className="flex-1">
                  <div className="bg-white rounded-2xl p-5 border border-blue-200 relative shadow-sm">
                    {/* Speech bubble pointer */}
                    <div className="absolute left-0 top-6 w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-r-[12px] border-r-white -translate-x-3" />
                    
                    <h3 className="text-slate-900 mb-2">
                      Legal bits, but important ones.
                    </h3>
                    <motion.p 
                      key={astroMessage}
                      initial={{ opacity: 0.7 }}
                      animate={{ opacity: 1 }}
                      className="text-slate-600 leading-relaxed mb-3"
                    >
                      {typeof astroMessage === 'string' && astroMessage !== 'professional' 
                        ? astroMessage 
                        : "Take a quick glance — I've highlighted the key parts for you. These terms protect both you and OpenTurf during your onboarding journey. Don't worry, it's all standard stuff, but please do read through it."
                      }
                    </motion.p>

                    {/* Reading Stats */}
                    {hasStartedReading && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        className="flex items-center space-x-4 text-xs text-slate-500 pt-2 border-t border-slate-200"
                      >
                        <div className="flex items-center space-x-1">
                          <Eye className="w-3 h-3" />
                          <span>Est. 3-4 min read</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Star className="w-3 h-3 text-amber-500" />
                          <span>{keyPoints.filter(kp => readingSections.has(kp.section)).length} key points seen</span>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 h-full min-h-0">
            {/* Main Terms Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.6 }}
              className="lg:col-span-2 flex flex-col min-h-0"
            >
              <Card className="bg-white/80 backdrop-blur-sm border border-slate-200 overflow-hidden flex flex-col flex-1 min-h-0">
                <div className="p-6 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <h1 className="text-slate-900 mb-1">Terms & Conditions</h1>
                      <p className="text-slate-600 text-sm">
                        DayOne Candidate Onboarding Platform
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDownloadPDF}
                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Download PDF
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-3">
                    Last updated: {new Date().toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                </div>

                <ScrollArea 
                  className="h-[32rem] lg:h-[36rem] xl:h-[40rem]"
                  onScrollCapture={handleScroll}
                >
                  <div className="p-6 space-y-8 text-slate-700">
                    {/* Interactive Key Points Summary */}
                    <motion.div 
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg p-6 mb-8"
                    >
                      <div className="flex items-start space-x-3">
                        <Info className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <h4 className="text-amber-900 mb-3">📋 Key Points at a Glance</h4>
                          <div className="space-y-2">
                            {keyPoints.map((point, index) => (
                              <motion.div
                                key={point.id}
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: index * 0.1 }}
                                className="group"
                              >
                                <button
                                  onClick={() => toggleKeyPoint(point.id)}
                                  className="w-full text-left p-3 rounded-lg border border-amber-200 bg-white/50 hover:bg-white/80 transition-all duration-200"
                                >
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                      <Badge 
                                        variant={point.importance === 'high' ? 'destructive' : 'secondary'}
                                        className="text-xs"
                                      >
                                        {point.importance}
                                      </Badge>
                                      <span className="text-amber-800 group-hover:text-amber-900">
                                        {point.title}
                                      </span>
                                    </div>
                                    {expandedKeyPoint === point.id ? 
                                      <ChevronUp className="w-4 h-4 text-amber-600" /> :
                                      <ChevronDown className="w-4 h-4 text-amber-600" />
                                    }
                                  </div>
                                  
                                  <p className="text-amber-700 mt-2 opacity-80 leading-relaxed">
                                    {point.summary}
                                  </p>

                                  <AnimatePresence>
                                    {expandedKeyPoint === point.id && (
                                      <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="mt-3 pt-3 border-t border-amber-200"
                                      >
                                        <p className="text-amber-800 leading-relaxed">
                                          {point.details}
                                        </p>
                                      </motion.div>
                                    )}
                                  </AnimatePresence>
                                </button>
                              </motion.div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </motion.div>

                    <section>
                      <div className="flex items-center space-x-2 mb-3">
                        <motion.div 
                          className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs text-blue-700"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          1
                        </motion.div>
                        <h3 className="text-slate-900">Acceptance of Terms</h3>
                        {readingSections.has(1) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                          >
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          </motion.div>
                        )}
                      </div>
                      <p className="leading-relaxed text-slate-700">
                        By accessing and using the DayOne candidate onboarding platform ("Service"), 
                        you accept and agree to be bound by the terms and provisions of this agreement. 
                        This Service is provided by OpenTurf Technologies Pvt. Ltd.
                      </p>
                    </section>

                    <section>
                      <div className="flex items-center space-x-2 mb-3">
                        <motion.div 
                          className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs text-blue-700"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          2
                        </motion.div>
                        <h3 className="text-slate-900">Service Purpose & Scope</h3>
                        {readingSections.has(2) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                          >
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          </motion.div>
                        )}
                      </div>
                      <p className="mb-4 leading-relaxed text-slate-700">
                        DayOne facilitates seamless onboarding for new employees, consultants, and interns. 
                        The platform enables you to:
                      </p>
                      <ul className="list-disc ml-6 space-y-2 text-slate-700">
                        <li>Complete identity verification through secure OTP</li>
                        <li>Upload and manage required employment documents</li>
                        <li>Review AI-extracted information for accuracy</li>
                        <li>Complete role-specific onboarding workflows</li>
                        <li>Access your personalized welcome dashboard</li>
                      </ul>
                    </section>

                    {/* Highlighted Important Section */}
                    <motion.section 
                      className="bg-blue-50 border border-blue-200 rounded-lg p-4"
                      whileHover={{ scale: 1.01 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <div className="flex items-center space-x-2 mb-3">
                        <motion.div 
                          className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center text-xs text-white"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          3
                        </motion.div>
                        <h3 className="text-slate-900">Data Collection & Processing</h3>
                        <AlertCircle className="w-4 h-4 text-blue-600" />
                        {readingSections.has(3) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                          >
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          </motion.div>
                        )}
                      </div>
                      <p className="mb-4 leading-relaxed text-slate-700">
                        <strong className="text-blue-900">Important:</strong> By using this Service, you consent to the collection and processing of your personal information. We collect:
                      </p>
                      <ul className="list-disc ml-6 space-y-2 text-slate-700">
                        <li>Personal identification (name, address, contact details, photos)</li>
                        <li>Employment documents (ID proofs, educational certificates, experience letters)</li>
                        <li>Professional information (skills, qualifications, references)</li>
                        <li>Communication logs and system interaction data</li>
                      </ul>
                    </motion.section>

                    <section>
                      <div className="flex items-center space-x-2 mb-3">
                        <motion.div 
                          className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs text-blue-700"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          4
                        </motion.div>
                        <h3 className="text-slate-900">AI-Powered Document Processing</h3>
                        {readingSections.has(4) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                          >
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          </motion.div>
                        )}
                      </div>
                      <p className="mb-4 leading-relaxed text-slate-700">
                        Our Service utilizes TurfAI technology for intelligent document extraction and validation. 
                        You acknowledge that:
                      </p>
                      <ul className="list-disc ml-6 space-y-2 text-slate-700">
                        <li>AI processing may not achieve 100% accuracy and requires human verification</li>
                        <li>You are responsible for reviewing and confirming all extracted information</li>
                        <li>We continuously improve our AI models using anonymized data patterns</li>
                        <li>Manual verification may be required for low-confidence extractions</li>
                      </ul>
                    </section>

                    <motion.section 
                      className="bg-green-50 border border-green-200 rounded-lg p-4"
                      whileHover={{ scale: 1.01 }}
                      transition={{ type: "spring", stiffness: 300 }}
                    >
                      <div className="flex items-center space-x-2 mb-3">
                        <motion.div 
                          className="w-6 h-6 bg-green-600 rounded-full flex items-center justify-center text-xs text-white"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          5
                        </motion.div>
                        <h3 className="text-slate-900">Your Responsibilities</h3>
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                        {readingSections.has(5) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                          >
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          </motion.div>
                        )}
                      </div>
                      <p className="mb-4 leading-relaxed text-slate-700">You agree to:</p>
                      <ul className="list-disc ml-6 space-y-2 text-slate-700">
                        <li>Provide accurate, complete, and truthful information</li>
                        <li>Upload only genuine and legally valid documents</li>
                        <li>Maintain the confidentiality of your account credentials</li>
                        <li>Report any suspicious activity or security concerns immediately</li>
                        <li>Complete the onboarding process within the specified timeframe</li>
                        <li>Respond promptly to verification requests from HR</li>
                      </ul>
                    </motion.section>

                    {/* Continue with remaining sections... */}
                    <section>
                      <div className="flex items-center space-x-2 mb-3">
                        <motion.div 
                          className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs text-blue-700"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          6
                        </motion.div>
                        <h3 className="text-slate-900">Data Security & Privacy</h3>
                        {readingSections.has(6) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                          >
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          </motion.div>
                        )}
                      </div>
                      <p className="leading-relaxed text-slate-700">
                        We implement enterprise-grade security measures including end-to-end encryption, 
                        secure cloud infrastructure, and regular security audits. Your data is encrypted 
                        both at rest and in transit. Access is restricted to authorized personnel only.
                      </p>
                    </section>

                    <section>
                      <div className="flex items-center space-x-2 mb-3">
                        <motion.div 
                          className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs text-blue-700"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          7
                        </motion.div>
                        <h3 className="text-slate-900">Data Retention Policy</h3>
                        {readingSections.has(7) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                          >
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          </motion.div>
                        )}
                      </div>
                      <p className="leading-relaxed text-slate-700">
                        We retain your personal information only as long as necessary for onboarding 
                        purposes and as required by applicable laws. Upon successful completion, 
                        certain data may be transferred to your employer's HR systems with appropriate 
                        security protocols.
                      </p>
                    </section>

                    <section>
                      <div className="flex items-center space-x-2 mb-3">
                        <motion.div 
                          className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs text-blue-700"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          8
                        </motion.div>
                        <h3 className="text-slate-900">Limitation of Liability</h3>
                        {readingSections.has(8) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                          >
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          </motion.div>
                        )}
                      </div>
                      <p className="leading-relaxed text-slate-700">
                        To the fullest extent permitted by law, OpenTurf Technologies Pvt. Ltd. 
                        shall not be liable for any indirect, incidental, special, consequential, 
                        or punitive damages arising from your use of the Service.
                      </p>
                    </section>

                    <section>
                      <div className="flex items-center space-x-2 mb-3">
                        <motion.div 
                          className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center text-xs text-blue-700"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          9
                        </motion.div>
                        <h3 className="text-slate-900">Governing Law & Jurisdiction</h3>
                        {readingSections.has(9) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                          >
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          </motion.div>
                        )}
                      </div>
                      <p className="leading-relaxed text-slate-700">
                        These terms are governed by the laws of India. Any disputes shall be 
                        subject to the exclusive jurisdiction of courts in Bangalore, Karnataka, India.
                      </p>
                    </section>

                    <section className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-3">
                        <motion.div 
                          className="w-6 h-6 bg-slate-600 rounded-full flex items-center justify-center text-xs text-white"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          10
                        </motion.div>
                        <h3 className="text-slate-900">Contact Information</h3>
                        {readingSections.has(10) && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                          >
                            <CheckCircle2 className="w-4 h-4 text-green-600" />
                          </motion.div>
                        )}
                      </div>
                      <p className="mb-4 leading-relaxed text-slate-700">
                        Questions about these Terms & Conditions? Contact us:
                      </p>
                      <div className="bg-white rounded-lg p-4">
                        <p><strong>OpenTurf Technologies Pvt. Ltd.</strong></p>
                        <p>Email: legal@openturf.com</p>
                        <p>Support: support@openturf.com</p>
                        <p>Phone: +91-80-4567-8900</p>
                        <p>Address: Bangalore, Karnataka, India</p>
                      </div>
                    </section>

                    {/* Reading completion achievement */}
                    {readingProgress >= 95 && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="text-center py-6"
                      >
                        <div className="inline-flex items-center space-x-3 text-green-600 bg-green-50 px-6 py-3 rounded-full border border-green-200">
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1 }}
                          >
                            <Sparkles className="w-5 h-5" />
                          </motion.div>
                          <span>Excellent! You've read everything thoroughly</span>
                          <CheckCircle2 className="w-5 h-5" />
                        </div>
                      </motion.div>
                    )}
                  </div>
                </ScrollArea>
              </Card>
            </motion.div>

            {/* Enhanced Sidebar */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
              className="space-y-6"
            >
              {/* Reading Stats */}
              {hasStartedReading && (
                <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
                  <h3 className="text-slate-900 mb-4 flex items-center space-x-2">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                    <span>Reading Progress</span>
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-slate-600">Completion</span>
                        <span className="text-blue-600 font-medium">{Math.round(completionPercentage)}%</span>
                      </div>
                      <Progress value={completionPercentage} className="h-2" />
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div className="text-center p-3 bg-white/50 rounded-lg">
                        <div className="text-lg text-slate-900">{readingSections.size}</div>
                        <div className="text-xs text-slate-600">sections read</div>
                      </div>
                      <div className="text-center p-3 bg-white/50 rounded-lg">
                        <div className="text-lg text-slate-900">{formatReadingTime(readingTime)}</div>
                        <div className="text-xs text-slate-600">time spent</div>
                      </div>
                    </div>
                  </div>
                </Card>
              )}

              {/* Quick Links */}
              <Card className="p-6 bg-white/80 backdrop-blur-sm border border-slate-200">
                <h3 className="text-slate-900 mb-4">Quick Links</h3>
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={onShowPrivacyPolicy}
                    className="w-full justify-start text-blue-600 border-blue-200 hover:bg-blue-50"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    View Data Privacy Policy
                    <ExternalLink className="w-3 h-3 ml-auto" />
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadPDF}
                    className="w-full justify-start text-slate-600 hover:bg-slate-50"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Full PDF
                  </Button>
                </div>
              </Card>

              {/* Enhanced Acceptance Section */}
              <Card className="p-6 bg-white/80 backdrop-blur-sm border border-slate-200">
                <h3 className="text-slate-900 mb-4">Agreement</h3>
                
                <div className="space-y-4">
                  <motion.div 
                    className="flex items-start space-x-3"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <Checkbox
                      id="terms-acceptance"
                      checked={hasAccepted}
                      onCheckedChange={(checked) => setHasAccepted(checked === true)}
                      className="mt-1"
                    />
                    <label 
                      htmlFor="terms-acceptance"
                      className="text-sm text-slate-700 leading-relaxed cursor-pointer"
                    >
                      I have read and agree to the Terms & Conditions
                    </label>
                  </motion.div>

                  <AnimatePresence>
                    {readingProgress < 50 && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="text-xs text-amber-600 bg-amber-50 p-3 rounded-lg border border-amber-200"
                      >
                        <div className="flex items-center space-x-2">
                          <Info className="w-4 h-4 flex-shrink-0" />
                          <span>Keep reading to enable the accept button</span>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <Button
                    onClick={handleAcceptTerms}
                    disabled={!hasAccepted || readingProgress < 50}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white disabled:bg-slate-300 disabled:text-slate-500 relative overflow-hidden group"
                  >
                    {hasAccepted && readingProgress >= 50 ? (
                      <>
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600"
                          initial={{ x: "-100%" }}
                          whileHover={{ x: "0%" }}
                          transition={{ duration: 0.3 }}
                        />
                        <span className="relative z-10 flex items-center">
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Accept & Continue
                        </span>
                      </>
                    ) : (
                      'Please read and accept terms'
                    )}
                  </Button>

                  <p className="text-xs text-slate-500 text-center">
                    By continuing, you confirm that you have read, understood, 
                    and agree to be bound by these terms.
                  </p>
                </div>
              </Card>

              {/* Security Notice */}
              <Card className="p-4 bg-blue-50 border border-blue-200">
                <div className="flex items-start space-x-3">
                  <Shield className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="text-blue-900 text-sm mb-1">Secure & Confidential</h4>
                    <p className="text-xs text-blue-700">
                      Your information is protected with enterprise-grade security 
                      and will only be used for onboarding purposes.
                    </p>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.5 }}
        className="relative z-10 p-6 border-t border-gray-200/50 bg-white/70 backdrop-blur-sm"
      >
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-slate-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-slate-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-slate-400 rounded-full"></span>
            <span className="text-blue-600">Legal Agreement</span>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}